import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import MuiButton from '@mui/material/Button';
import { toast } from '../../../ui/toast';
import { DIALOG_TYPE } from '../../../constants';
import { RefreshListIcons } from '../../../icons/icons';
import {
  ContextMenu,
  DataTable,
  Dialog,
  FilterSearch,
  MainLayout,
} from '../../../ui';
import { deleteCandidate } from '../../../utils/api_service/candidate';
import { fetchOrganizationUsers } from '../../../utils/redux_store/features/organization_user_reducer';
import { store } from '../../../utils/redux_store/store';
import { OrganizationUser } from '../../../utils/types/admin_types';
import { SettingsSubNav } from '../components/SettingsSubNav';
import { getColumns } from './Functions';
import { getMenuItemsForMenu } from './Functions/GetContextMenuItems';
import { useCandidatemanagement } from './Hooks';

export const CandidateManagement: React.FC = () => {
  const {
    pageNumber,
    totalPages,
    loading,
    candidateList,
    onPageChange,
    handleSearchTermChange,
    setCandidateList,
    totalRecords,
  } = useCandidatemanagement();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [contextMenuVisible, setContextMenuVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState<OrganizationUser | null>(null);
  const [deleteConfirmState, setDeleteConfirmState] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] =
    useState<OrganizationUser | null>(null);

  const handleOpenMenu = (
    e: React.MouseEvent<HTMLButtonElement>,
    row: OrganizationUser,
  ) => {
    setAnchorEl(e.currentTarget);
    setSelectedRow(row);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const openDeleteDialog = (employee: OrganizationUser) => {
    setEmployeeToDelete(employee);
    setDeleteConfirmState(true);
  };

  const contextMenuItems = getMenuItemsForMenu({
    onDelete: (row: OrganizationUser) => {
      openDeleteDialog(row);
    },
  });

  const handleDeleteEmployee = async () => {
    if (!employeeToDelete) return;
    try {
      const response = await deleteCandidate(employeeToDelete.id);
      if (response.ok) {
        const updatedEmployees = candidateList.filter(
          (employee) => employee.id !== employeeToDelete.id,
        );
        setCandidateList(updatedEmployees);
        toast.success('Employee deleted.', { position: 'bottom-center' });
      } else {
        const errorData = await response.json();
        toast.error(errorData.error, { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('Error deleting employee: ' + error, {
        position: 'bottom-center',
      });
    } finally {
      setDeleteConfirmState(false);
      setEmployeeToDelete(null);
    }
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <SettingsSubNav currentPath='/candidate-management' />
      <MainLayout>
        {/* Search & Actions Bar */}
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2, mb: 2 }}>
          <FilterSearch
            onSearch={handleSearchTermChange}
            searchText='Search by name, email'
          />
          <MuiButton
            variant='outlined'
            startIcon={<RefreshListIcons style={{ width: 18, height: 18 }} />}
            onClick={() => store.dispatch(fetchOrganizationUsers())}
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: 'text.secondary',
              borderColor: 'divider',
              px: 2,
              py: 0.85,
              bgcolor: 'background.paper',
              '&:hover': {
                bgcolor: 'action.hover',
                borderColor: 'action.active',
              },
            }}
          >
            Refresh
          </MuiButton>
        </Box>

        {/* Data Table Container */}
        <Paper
          variant='outlined'
          sx={{
            borderRadius: '6px',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            overflow: 'hidden',
            width: '100%',
            minHeight: 360,
          }}
        >
          <DataTable
            isLoading={loading}
            columns={getColumns({
              handleOpenMenu,
              handleCloseMenu,
              anchorEl,
            })}
            data={candidateList}
            rowsPerPage={10}
            onPageChange={onPageChange}
            currentPage={pageNumber}
            totalPages={totalPages}
            totalRecords={totalRecords}
          />
        </Paper>
      </MainLayout>

      {anchorEl && selectedRow && (
        <ContextMenu<OrganizationUser>
          anchorEl={anchorEl}
          onClose={handleCloseMenu}
          row={selectedRow}
          menuItems={contextMenuItems}
        />
      )}

      <Dialog
        title='Are you sure you want to delete this employee?'
        handleApply={handleDeleteEmployee}
        confirmButtonLable='Yes, Delete'
        isOpen={deleteConfirmState}
        onClose={() => setDeleteConfirmState(false)}
        cancelButtonLable='Cancel'
        dialogType={DIALOG_TYPE.ERROR}
        justify='end'
      >
        <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
          This action cannot be undone, changes that you have made will be lost.
        </Typography>
      </Dialog>
    </Box>
  );
};
